'use client';

import assert from 'assert';
import type { ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Text } from '@savvy-kit/foundations/text';
import PageContainer from '@savvy-kit/templates/page-container';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ErrorBoundary } from 'react-error-boundary';
import { useReportIssueModal } from 'src/components/advisor-dashboard/report-issue/use-report-issue-modal';
import AdvisorDashboardCard from 'src/components/advisor-dashboard/views/advisor-dashboard-card';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import { graphql } from 'src/gql';

import { HOUSEHOLD_STAGE_INACTIVE } from '@savvy/constants/household-stages';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { useOverviewRailPreference } from '../common/use-overview-rail-preference';
import { InactiveHouseholdOverlayWrapper } from '../household/inactive-household-overlay-wrapper';
import ClientPageTitleNav from '../nav/client-page-title-nav/client-page-title-nav';
import { HouseholdOverviewRailPreferenceProvider } from './household-overview-rail-preference-context';
import { PageBreadcrumbProvider } from './page-breadcrumb-context';

export const ADVISOR_DASH_HOUSEHOLD_QUERY = graphql(/* GraphQL */ `
  query AdvisorDashHousehold($salesforceAccountId: String!, $includeBookOnboardingCockpit: Boolean!) {
    household(salesforceAccountId: $salesforceAccountId) {
      householdStage
      ...HouseholdAccount
    }
  }
`);

type ClientHouseholdLayoutProps = {
  readonly children?: ReactNode;
};

export function ClientHouseholdLayout({ children }: ClientHouseholdLayoutProps) {
  const router = useRouter();
  const salesforceAccountId = router.query['salesforceAccountId'];
  assert(
    typeof salesforceAccountId === 'string',
    `salesforceAccountId must be a string, but was typeof ${typeof salesforceAccountId}`,
  );

  const flags = useFlags();
  const servalSupport = Boolean(flags['flagServalSupport']);
  const includeBookOnboardingCockpit = Boolean(flags['flagAdvisorOnboarding']);
  const openReportIssueModal = useReportIssueModal((s) => s.openReportIssueModal);

  const { data: householdData, refetch: refetchHouseholdData } = useQueryWithErrorLogging(
    ADVISOR_DASH_HOUSEHOLD_QUERY,
    {
      variables: {
        salesforceAccountId,
        includeBookOnboardingCockpit,
      },
    },
  );

  const isInactive = householdData?.household.householdStage === HOUSEHOLD_STAGE_INACTIVE;

  // Lifted here because the gear and the Overview rail aren't in the same subtree; costs one
  // `userSavedViews` read per household route load, and the surfaces on top stay flag-gated.
  const overviewRailPreference = useOverviewRailPreference({
    storageKey: LOCAL_STORAGE_KEYS.HOUSEHOLD_OVERVIEW_RAIL_PREFERENCE,
  });

  return (
    <PageBreadcrumbProvider>
      <HouseholdOverviewRailPreferenceProvider value={overviewRailPreference}>
        <ClientPageTitleNav
          householdFragment={householdData?.household}
          refetchHouseholdData={refetchHouseholdData}
          salesforceAccountId={salesforceAccountId}
        />
        <PageContainer>
          <ErrorBoundary
            fallback={
              <AdvisorDashboardCard sx={{ p: 2, m: 4.5 }}>
                <Text variant="h4">
                  {servalSupport ? (
                    <>
                      Looks like we&apos;re having an issue loading this household. Please{' '}
                      <button type="button" onClick={() => openReportIssueModal()} className="underline">
                        create a support request
                      </button>{' '}
                      for assistance.
                    </>
                  ) : (
                    <>
                      Looks like we&apos;re having an issue loading this household. Please reach out to @product-goalie
                      in Slack for support.
                    </>
                  )}
                </Text>
              </AdvisorDashboardCard>
            }
            onError={(error, info) => {
              console.error(error, info);
            }}
          >
            {isInactive ? <InactiveHouseholdOverlayWrapper>{children}</InactiveHouseholdOverlayWrapper> : children}
          </ErrorBoundary>
        </PageContainer>
      </HouseholdOverviewRailPreferenceProvider>
    </PageBreadcrumbProvider>
  );
}
